import { select, put } from "redux-saga/effects";
import Alert from "react-s-alert";
import Session from "session";
import get from "lodash/get";
import {
  resetSession,
  pathWithChannel,
  gotoUrl,
  formatMessage
} from "utils/helpers";
import {
  IS_PATHS_SECURED,
  paths,
  RECAPTURE_TOKEN_BROWSER_ERROR,
  SESSION_TIMEOUT,
  TOKEN_INVALID_HTTP_CODE,
  UNAUTHORIZED_HTTP_CODE
} from "utils/constants";
import { getDeviceId, isWebview } from "utils/localStorage";
import includes from "lodash/includes";
import {
  genericErrorSelector,
  errorSelectorByKey,
  selectedNumberSelector,
  localeSelector,
  selectedNumberTypeSelector,
  errorCodeListSelector
} from "ducks/selectors";
import { clearData } from "ducks/actions";
/**
 * Returns true if the error is invlaid, else false
 * @param {object} errorObj
 */
export function isTokenInvalidError(errorObj = {}) {
  // Token validation is not applicable for countries that has not user authentication
  if (!IS_PATHS_SECURED) return false;

  const { status } = errorObj;
  return status === TOKEN_INVALID_HTTP_CODE;
}

/* Getting the error code message from the errorCodeListSelector. */
export function* getErrorCodeMessage(
  error = {},
  message = "something went wrong"
) {
  const errorCodeList = yield select(errorCodeListSelector);
  const locale = yield select(localeSelector);
  const { errorCode } = error;
  return formatMessage(
    get(locale, errorCodeList[errorCode || error] || errorCode, message)
  );
}

/**
 * Returns true if the error is because of token expiry and need a refresh token call, else false
 * @param {object} errorObj
 */
export function isTokenRefreshRequired(errorObj = {}) {
  // Token refresh is not applicable for countries that has not user authentication
  if (!IS_PATHS_SECURED) return false;

  const { status } = errorObj;
  return status === UNAUTHORIZED_HTTP_CODE;
}

export function isRetryableRecaptchaError(errorObj = {}) {
  const { response: { result: { code } = {} } = {} } = errorObj;
  return code === RECAPTURE_TOKEN_BROWSER_ERROR;
}

/**
 * Validate session is only done if path security is not in place.
 * Validate session based on deviceid. DeviceId is considered to be a timestamp here
 */
export function validateSession() {
  if (IS_PATHS_SECURED) return;
  const sessionDuration = getDeviceId() ? Session.minutes() : 0;
  if (sessionDuration && sessionDuration > SESSION_TIMEOUT) {
    Alert.warning("Your session has been reset");
    resetSession();
  }
}

/* Checking if the number is locked and if it is locked, it is checking if the lock has expired. If the
lock has expired, it is clearing the data and redirecting to the connection details page. */
export function* validateNumberLockSession() {
  const selectedNumber = yield select(selectedNumberSelector);
  const selectedNumberType = yield select(selectedNumberTypeSelector);
  const numberLockExpire = get(selectedNumber, "lock_expire_at", null);
  if (numberLockExpire && !get(selectedNumberType, "isPortIn")) {
    const now = new Date();
    const lockExpireTime = new Date(numberLockExpire * 1000);
    if (lockExpireTime.getTime() < now.getTime()) {
      const locale = yield select(localeSelector);
      Alert.warning(
        get(
          locale,
          "number_lock_expired",
          "Number you have selected is not available"
        )
      );
      yield put(clearData(["numbers", "lockedNumber"]));
      gotoUrl(pathWithChannel(paths.CONNECTION_DETAILS), "web", {}, true);
    }
  }
}

/**
 * Handles the redirection after user logout
 */
export function logoutUserCallback() {
  if (isWebview()) {
    gotoUrl(paths.LOGGED_OUT, false, {}, true, { reset: true });
  } else {
    gotoUrl(pathWithChannel(`${paths.LOG_IN}`, "web"), false, {}, true, {
      reset: true
    });
  }
}

/**
 * Check for errors that are whitelisted. these error codes are expected and will not be shown to the user
 * @param {object} errorBody
 */
export function isWhiteListedError(whitelistedErrorCodesList, errorBody = {}) {
  if (errorBody === null) {
    return false;
  }
  const { errorId } = errorBody;
  return includes(whitelistedErrorCodesList, errorId);
}

/**
 * Returns the user message for consumer, according to the locale
 * @param {object} failureObject failure response object from API
 * @returns User Message
 */
export function* getErrorMessageForCode(failureObject = {}) {
  const { title: errorTitle } = failureObject;
  // Return undefined if errorTitle is not available.
  if (!errorTitle) return;

  const memoizedSelector = yield select(state => errorSelectorByKey(state));
  const errorMessageForKey = memoizedSelector(errorTitle);

  if (errorMessageForKey) {
    return errorMessageForKey;
  } else {
    const genericErrorMessage = yield select(genericErrorSelector);
    return genericErrorMessage;
  }
}
